// Replaces every native <select> with MALVO's custom dropdown (search for long lists).
// The native element stays in the DOM (hidden) so React state and forms keep working.
const CHEVRON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';
const CHECK = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
const triggers = new WeakMap<HTMLSelectElement, HTMLButtonElement>();
const live = new Set<HTMLSelectElement>();
let menu: HTMLDivElement | null = null, openFor: HTMLSelectElement | null = null;
const valueSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')!.set!;
const de = () => (document.documentElement.lang || navigator.language || '').startsWith('de');

function label(sel: HTMLSelectElement) { const o = sel.selectedOptions[0] || sel.options[0]; return o ? o.textContent || '' : ''; }
function sync(sel: HTMLSelectElement) {
  const t = triggers.get(sel); if (!t) return;
  const v = t.querySelector('.ms-value')!; const l = label(sel);
  if (v.textContent !== l) v.textContent = l;
  t.disabled = sel.disabled;
  const cls = 'custom-select-trigger ms-trigger ' + sel.className; if (t.className.replace(' is-open', '') !== cls) t.className = cls + (openFor === sel ? ' is-open' : '');
}
function close() { if (!menu) return; menu.remove(); menu = null; if (openFor) triggers.get(openFor)?.classList.remove('is-open'); openFor = null; }
function pick(sel: HTMLSelectElement, value: string) {
  valueSetter.call(sel, value);
  sel.dispatchEvent(new Event('input', { bubbles: true }));
  sel.dispatchEvent(new Event('change', { bubbles: true }));
  sync(sel); close(); triggers.get(sel)?.focus();
}
function open(sel: HTMLSelectElement) {
  if (openFor === sel) return close();
  close();
  const t = triggers.get(sel)!; const r = t.getBoundingClientRect();
  openFor = sel; t.classList.add('is-open');
  const m = document.createElement('div'); menu = m;
  m.className = 'custom-select-menu ms-menu'; m.setAttribute('role', 'listbox');
  const opts = [...sel.options].filter(o => !o.hidden);
  let search: HTMLInputElement | null = null;
  if (opts.length > 8) {
    search = document.createElement('input'); search.className = 'ms-search'; search.type = 'search';
    search.placeholder = de() ? 'Suchen …' : 'Search…'; m.appendChild(search);
  }
  const list = document.createElement('div'); list.className = 'ms-list'; m.appendChild(list);
  let active = Math.max(0, opts.findIndex(o => o.selected));
  const render = () => {
    const q = (search?.value || '').trim().toLowerCase();
    list.innerHTML = '';
    const shown = opts.filter(o => !q || (o.textContent || '').toLowerCase().includes(q));
    if (!shown.length) { const e = document.createElement('div'); e.className = 'ms-empty'; e.textContent = de() ? 'Nichts gefunden' : 'No results'; list.appendChild(e); }
    shown.forEach((o, i) => {
      const b = document.createElement('button'); b.type = 'button'; b.setAttribute('role', 'option');
      b.className = 'custom-select-option' + (o.selected ? ' is-selected' : '') + (i === active ? ' is-active' : '');
      b.disabled = o.disabled; b.setAttribute('aria-selected', String(o.selected));
      const s = document.createElement('span'); s.textContent = o.textContent || ''; b.appendChild(s);
      if (o.selected) b.insertAdjacentHTML('beforeend', CHECK);
      b.onclick = () => pick(sel, o.value);
      list.appendChild(b);
    });
    return shown;
  };
  let shown = render();
  search?.addEventListener('input', () => { active = 0; shown = render(); });
  m.addEventListener('keydown', e => {
    if (e.key === 'Escape') { e.preventDefault(); close(); t.focus(); }
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(0, Math.min(shown.length - 1, active + (e.key === 'ArrowDown' ? 1 : -1))); shown = render(); list.children[active]?.scrollIntoView({ block: 'nearest' }); }
    else if (e.key === 'Enter' && shown[active]) { e.preventDefault(); pick(sel, shown[active].value); }
  });
  document.body.appendChild(m);
  const h = Math.min(320, m.scrollHeight), below = innerHeight - r.bottom - 8;
  m.style.cssText = `position:fixed;left:${Math.max(8, Math.min(r.left, innerWidth - Math.max(r.width, 180) - 8))}px;width:${Math.max(r.width, 180)}px;max-height:${Math.max(160, Math.min(320, below > h || below > r.top ? below : r.top - 8))}px;z-index:10000;right:auto;` + (below > h || below > r.top ? `top:${r.bottom + 4}px;bottom:auto` : `top:auto;bottom:${innerHeight - r.top + 4}px`);
  (search || list.querySelector<HTMLElement>('.is-selected') || list.firstElementChild as HTMLElement)?.focus?.();
  list.querySelector('.is-selected')?.scrollIntoView({ block: 'nearest' });
}
function enhance(sel: HTMLSelectElement) {
  if (triggers.has(sel) || sel.multiple || (sel.size > 1) || sel.dataset.native !== undefined || sel.closest('[data-native-select]')) return;
  const t = document.createElement('button'); t.type = 'button';
  t.innerHTML = '<span class="ms-value custom-select-value"></span>' + CHEVRON.replace('<svg', '<svg class="custom-select-chevron"');
  const aria = sel.getAttribute('aria-label') || sel.title; if (aria) t.setAttribute('aria-label', aria);
  t.setAttribute('aria-haspopup', 'listbox');
  t.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); open(sel); });
  t.addEventListener('keydown', e => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(sel); } });
  sel.classList.add('ms-native'); sel.tabIndex = -1; sel.setAttribute('aria-hidden', 'true');
  sel.insertAdjacentElement('afterend', t);
  triggers.set(sel, t); live.add(sel); sync(sel);
  sel.addEventListener('change', () => sync(sel));
}
function scan(root: ParentNode = document) { root.querySelectorAll?.('select').forEach(s => enhance(s as HTMLSelectElement)); }
function sweep() {
  for (const sel of live) {
    const t = triggers.get(sel)!;
    if (!sel.isConnected) { t.remove(); live.delete(sel); if (openFor === sel) close(); continue; }
    if (t.previousElementSibling !== sel) sel.insertAdjacentElement('afterend', t); // React moved/re-rendered siblings
    sync(sel);
  }
}
if (typeof window !== 'undefined' && typeof MutationObserver !== 'undefined') {
  let queued = false;
  const flush = () => { queued = false; scan(); sweep(); };
  new MutationObserver(() => { if (!queued) { queued = true; requestAnimationFrame(flush); } }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('pointerdown', e => { if (menu && !menu.contains(e.target as Node) && !(openFor && triggers.get(openFor)?.contains(e.target as Node))) close(); }, true);
  window.addEventListener('resize', close);
  document.addEventListener('scroll', e => { if (menu && !menu.contains(e.target as Node)) close(); }, true);
  setInterval(sweep, 800);
  if (document.readyState !== 'loading') flush(); else document.addEventListener('DOMContentLoaded', flush);
}
export {};
